import { useState, useRef, useEffect } from 'react';
import { MicrophoneIcon, StopCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

/**
 * VoiceChatButton - Speech-to-Text input component
 *
 * Uses OpenAI Whisper API via backend for context-aware voice recognition
 * Automatically corrects domain-specific terms based on knowledge base
 */
const VoiceChatButton = ({ onTranscript, disabled = false, isProcessing = false }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Check browser support for MediaRecorder
  useEffect(() => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      console.warn('MediaRecorder not supported in this browser');
      setIsSupported(false);
    }
  }, []);

  // Start recording
  const startRecording = async () => {
    try {
      setError(null);
      audioChunksRef.current = [];

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());

        // Process recording
        await processRecording();
      };

      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      console.log('🎤 Started recording...');

    } catch (error) {
      console.error('Failed to start recording:', error);
      if (error.name === 'NotAllowedError') {
        setError('Microphone access denied. Please enable microphone permissions.');
      } else {
        setError('Failed to start recording. Please try again.');
      }
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      console.log('🎤 Stopped recording');
    }
  };

  // Process recorded audio and send to backend
  const processRecording = async () => {
    try {
      // Create audio blob from chunks
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

      console.log('🎤 Processing audio:', audioBlob.size, 'bytes');

      // Show processing state
      setTranscript('Processing...');

      // Create FormData for upload
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      // Get auth token
      const token = localStorage.getItem('auth_token');

      // Send to backend for transcription
      const response = await axios.post(
        '/api/customer/voice/speech-to-text',
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { transcript, corrected } = response.data;

      console.log('✅ Transcript received:', transcript);
      if (corrected) {
        console.log('🔧 Context-aware corrections applied');
      }

      // Show transcript briefly
      setTranscript(transcript);

      // Send to parent component
      onTranscript(transcript);

      // Clear after 2 seconds
      setTimeout(() => {
        setTranscript('');
      }, 2000);

    } catch (error) {
      console.error('Transcription error:', error);
      setError(error.response?.data?.detail || 'Failed to transcribe audio');
      setTranscript('');
    }
  };

  // Toggle recording
  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // Don't render if not supported
  if (!isSupported) {
    return (
      <div className="text-sm text-gray-500 italic">
        Voice input not supported in this browser
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Microphone Button */}
      <button
        onClick={toggleRecording}
        disabled={disabled || isProcessing || transcript === 'Processing...'}
        className={`
          p-3 rounded-full transition-all duration-200
          ${isRecording
            ? 'bg-red-500 hover:bg-red-600 animate-pulse'
            : 'bg-blue-500 hover:bg-blue-600'
          }
          ${(disabled || isProcessing || transcript === 'Processing...') ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          text-white shadow-lg hover:shadow-xl
        `}
        title={isRecording ? 'Stop recording' : 'Start recording'}
      >
        {transcript === 'Processing...' || isProcessing ? (
          <ArrowPathIcon className="w-6 h-6 animate-spin" />
        ) : isRecording ? (
          <StopCircleIcon className="w-6 h-6" />
        ) : (
          <MicrophoneIcon className="w-6 h-6" />
        )}
      </button>

      {/* Transcript Display */}
      {transcript && transcript !== 'Processing...' && (
        <div className="max-w-xs text-sm text-gray-700 bg-green-50 border border-green-200 px-4 py-2 rounded-lg">
          <p className="italic">"{transcript}"</p>
          <p className="text-xs text-green-600 mt-1">✓ Context-aware correction applied</p>
        </div>
      )}

      {/* Processing Status */}
      {transcript === 'Processing...' && (
        <div className="text-xs text-gray-500 animate-pulse">
          Transcribing with AI...
        </div>
      )}

      {/* Recording Status */}
      {isRecording && (
        <div className="text-xs text-red-500 animate-pulse flex items-center gap-1">
          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
          Recording...
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="max-w-xs text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">
          {error}
        </div>
      )}

      {/* Info Text */}
      <div className="text-xs text-gray-400 text-center max-w-xs">
        Context-aware voice input powered by Whisper AI
      </div>
    </div>
  );
};

export default VoiceChatButton;
